import { useEffect } from "react";
import {
  LuArrowLeft,
  LuBug,
  LuCode,
  LuFileDown,
  LuImage,
  LuRotateCw,
  LuSettings,
  LuSmartphone,
  LuUpload,
} from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import { ProjectModalProvider } from "../../providers/ProjectModalProvider";
import {
  DashboardMode,
  useDashboardControlsActions,
  useDashboardMode,
  useWorkspaceSection,
  WorkspaceSection,
} from "../../view_stores/useDashboardControlsStore";
import { useProjectName } from "../../view_stores/useProjectConfigStore";
import {
  useProjectPreviewActions,
  useProjectPreviewUrl,
} from "../../view_stores/useProjectPreviewStore";
import { ProjectPreviewScreenshotControls } from "../projectPreview/components/ProjectPreviewScreenshotControls";
import { ProjectPreview } from "../projectPreview/ProjectPreview";
import { SideMenuIcon } from "../sideMenu/SideMenuIcon";
import { ProjectChat } from "./components/ProjectChat";
import { ProjectWorkspaceContent } from "./components/ProjectWorkspaceContent";
import { ProjectWorkspaceMenu } from "./components/ProjectWorkspaceMenu";

export function ProjectPageCompact() {
  const { setDashboardMode } = useDashboardControlsActions();
  const previewUrl = useProjectPreviewUrl();

  useEffect(() => {
    if (previewUrl) {
      setDashboardMode(DashboardMode.PREVIEW);
    }
  }, [previewUrl]);

  return (
    <>
      <Dashboard />
      <MainView />
    </>
  );
}

function Dashboard() {
  const dashboardMode = useDashboardMode();

  return dashboardMode ? (
    <div
      className="fixed inset-0 z-50 flex flex-col h-[100dvh]
    bg-light-surface-primary dark:bg-dark-surface-tertiary"
    >
      <div
        className="w-full h-full relative
      flex flex-col"
      >
        <ProjectModalProvider>
          <DashboardAppBar />
          <div className="flex-1 overflow-hidden">
            {dashboardMode === DashboardMode.PREVIEW && <ProjectPreview />}
            {dashboardMode === DashboardMode.WORKSPACE && (
              <ProjectWorkspaceContent />
            )}
          </div>
          <Tooltip
            id="dashboard-tooltip"
            className="max-w-[90%]"
            place="top"
            opacity={1.0}
          />
        </ProjectModalProvider>
      </div>
    </div>
  ) : null;
}

function DashboardAppBar() {
  const dashboardMode = useDashboardMode();
  const workspaceSection = useWorkspaceSection();
  const { setDashboardMode, setWorkspaceSection, goBackInCompactView } =
    useDashboardControlsActions();
  const { reload: reloadPreview } = useProjectPreviewActions();

  const getWorkspaceSectionName = () => {
    switch (workspaceSection) {
      // case WorkspaceSection.OVERVIEW:
      // return "Overview";
      case WorkspaceSection.ICON:
        return "App Icon";
      case WorkspaceSection.CODE:
        return "Code";
      case WorkspaceSection.DEBUG:
        return "Debug";
      case WorkspaceSection.SETTINGS:
        return "App Settings";
      case WorkspaceSection.PUBLISH:
        return "Publish";
      case WorkspaceSection.EXPORT:
        return "Export";
    }
  };

  const getWorkspaceSectionIcon = () => {
    const iconSize = 30;
    switch (workspaceSection) {
      // case WorkspaceSection.OVERVIEW:
      // return <LuFileChartColumn size={iconSize} />;
      case WorkspaceSection.ICON:
        return <LuImage size={iconSize} />;
      case WorkspaceSection.CODE:
        return <LuCode size={iconSize} />;
      case WorkspaceSection.DEBUG:
        return <LuBug size={iconSize} />;
      case WorkspaceSection.SETTINGS:
        return <LuSettings size={iconSize} />;
      case WorkspaceSection.PUBLISH:
        return <LuUpload size={iconSize} />;
      case WorkspaceSection.EXPORT:
        return <LuFileDown size={iconSize} />;
    }
  };

  return (
    <div
      className="h-12 w-full 
            px-2 py-9 
            flex items-center justify-between gap-2 
            cursor-pointer"
    >
      <div className="flex items-center gap-4">
        <LuArrowLeft size={24} onClick={goBackInCompactView} />
        <div className="flex items-center gap-1">
          {getWorkspaceSectionIcon()}
          <span className="w-full flex justify-center items-center text-lg">
            {dashboardMode === DashboardMode.WORKSPACE &&
              getWorkspaceSectionName()}
          </span>
        </div>
      </div>
      {workspaceSection !== WorkspaceSection.PUBLISH && (
        <div className="flex items-center gap-2">
          {dashboardMode === DashboardMode.PREVIEW && (
            <div className="flex items-center gap-2">
              <ProjectPreviewScreenshotControls optionsPosition="bottom-center" />
              <button
                className="flex items-center gap-2
     bg-white/25 dark:bg-[#2A2B2C] rounded-full p-[10px]
     hover:brightness-125 hover:cursor-pointer
     transition-all duration-300"
                onClick={reloadPreview}
              >
                <LuRotateCw size={20} />
              </button>
            </div>
          )}
          <button
            className="flex items-center gap-2
     bg-primary-blue rounded-full
     px-4 py-[6px] text-white
     hover:brightness-125 hover:cursor-pointer
     transition-all duration-300"
            onClick={() => {
              setDashboardMode(DashboardMode.WORKSPACE);
              setWorkspaceSection(WorkspaceSection.PUBLISH);
            }}
          >
            <LuUpload size={20} />
            Publish
          </button>
        </div>
      )}
    </div>
  );
}

function MainView() {
  return (
    <div
      className="h-[100dvh] min-h-[500px] w-full 
    bg-[#F2F3FD] dark:bg-[#1E2023] 
    flex flex-col"
    >
      <MainViewAppBar />
      <ProjectChat />
    </div>
  );
}

function MainViewAppBar() {
  const projectName = useProjectName();
  const previewUrl = useProjectPreviewUrl();
  const { setDashboardMode } = useDashboardControlsActions();

  return (
    <div
      className="pt-[18px] pb-1 w-full px-2 
      flex flex-col gap-3
       bg-[#E0E3F2] dark:bg-[#2B2E32]
       border-b-2 border-white dark:border-[#373B40]"
    >
      <div className="flex items-center justify-between w-full">
        <SideMenuIcon />
        <div className="flex gap-2">
          <ProjectWorkspaceMenu />
          <button
            onClick={() => setDashboardMode(DashboardMode.PREVIEW)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md 
            font-medium text-light-text dark:text-dark-text
            bg-white dark:bg-[#191C1F]"
            disabled={!projectName || !previewUrl}
          >
            <LuSmartphone size={18} />
            Preview
          </button>
        </div>
      </div>
      <div
        className="text-lg px-1 pb-2
      whitespace-nowrap overflow-x-auto"
      >
        {projectName}
      </div>
    </div>
  );
}
